<template>
  <div class="date-container" :style="containerStyle">
    <!-- Today view -->
    <div v-if="dateType === 'today'" class="today-date">
      <div
        v-if="
          format === DATE_FORMAT.DOT_FORMAT ||
          format === DATE_FORMAT.ISO_FORMAT ||
          format === DATE_FORMAT.US_FORMAT ||
          format === DATE_FORMAT.EU_FORMAT
        "
        class="numeric-date"
      >
        {{ formattedDate }}
      </div>
      <div
        v-else-if="
          format === DATE_FORMAT.US_LONG ||
          format === DATE_FORMAT.EU_LONG ||
          format === DATE_FORMAT.WEEKDAY
        "
        class="text-date"
      >
        {{ formattedDate }}
      </div>
      <div v-else class="numeric-date">
        {{ formattedDate }}
      </div>
    </div>

    <!-- Week view -->
    <div v-else-if="dateType === 'week'" class="week-view">
      <div
        class="days-names"
        :style="{ marginBottom: `${app.date ? app.date.textSeparation : 10}px` }"
      >
        <div
          v-for="day in currentWeekDays"
          :key="day.date"
          class="day-name"
          :style="{
            fontSize: `${app.date && app.date.weekTextSize ? app.date.weekTextSize : 12}px`,
          }"
        >
          {{ day.dayName }}
        </div>
      </div>
      <div class="days-row">
        <div
          v-for="day in currentWeekDays"
          :key="day.date"
          class="day-number"
          :style="{ color: day.isCurrentDay ? highlightColor : fontColor }"
        >
          {{ day.dayNumber }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment-timezone';
  import { hex8ToRgba, isHex8 } from '@/helpers/utils';
  import { DATE_WIDGET_TYPE, DATE_FORMAT, DATE_TEXT_ALIGN } from '@/constants/dateWidget';

  export default {
    name: 'DateView',

    props: {
      app: {
        type: Object,
        required: true,
      },
    },

    data() {
      return {
        currentDate: new Date(),
        timer: null,
        DATE_FORMAT,
      };
    },

    mounted() {
      this.updateDate();
      this.timer = setInterval(this.updateDate, 60000); // Update every minute
    },

    beforeDestroy() {
      if (this.timer) {
        clearInterval(this.timer);
      }
    },

    methods: {
      updateDate() {
        this.currentDate = new Date();
      },
    },

    computed: {
      dateType() {
        return this.app.date ? this.app.date.type : DATE_WIDGET_TYPE.TODAY;
      },

      language() {
        return this.app.date ? this.app.date.language : 'en';
      },

      format() {
        return this.app.date ? this.app.date.format : DATE_FORMAT.EU_FORMAT;
      },

      fontColor() {
        const color = this.app.date ? this.app.date.fontColor : '#000000';

        return color && isHex8(color) ? hex8ToRgba(color) : color;
      },

      highlightColor() {
        const color = this.app.date ? this.app.date.highlightColor : '';

        return color && isHex8(color) ? hex8ToRgba(color) : color;
      },

      containerStyle() {
        const horizontalPadding =
          this.app.date?.horizontalPadding !== undefined ? this.app.date.horizontalPadding : 10;
        const isNotToday = this.dateType !== DATE_WIDGET_TYPE.TODAY;

        const justifyContent =
          !isNotToday && this.app.date?.alignment !== undefined
            ? this.app.date.alignment
            : 'center';

        const textAlign = DATE_TEXT_ALIGN[justifyContent] || 'center';

        return {
          color: this.fontColor,
          fontFamily: this.app.date ? this.app.date.textFont : 'Poppins',
          fontSize: `${this.app.date ? this.app.date.textSize : 12}px`,
          display: 'flex',
          flexDirection: isNotToday ? 'column' : 'row',
          justifyContent: justifyContent,
          textAlign,
          alignItems: 'center',
          width: '100%',
          height: '100%',
          paddingLeft: `${horizontalPadding}px`,
          paddingRight: `${horizontalPadding}px`,
        };
      },

      formattedDate() {
        moment.locale(this.language);
        const date = moment(this.currentDate);

        return date.format(this.format);
      },

      currentWeekDays() {
        moment.locale(this.language);
        const today = moment(this.currentDate);
        const startOfWeek = moment(today).startOf('week');

        const days = [];
        for (let i = 0; i < 7; i++) {
          const day = moment(startOfWeek).add(i, 'days');

          days.push({
            date: day.format('YYYY-MM-DD'),
            dayNumber: day.format('DD'),
            dayName: day.format('ddd').charAt(0).toUpperCase() + day.format('ddd').slice(1),
            isCurrentDay: day.isSame(today, 'day'),
          });
        }

        return days;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .date-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding-top: 10px;
    padding-bottom: 10px;
    box-sizing: border-box;

    .today-date {
      text-align: inherit;
      font-weight: bold;
    }

    .week-view {
      width: 100%;

      .week-name {
        text-align: center;
        font-weight: bold;
        margin-bottom: 10px;
        font-size: 1.2em;
      }

      .days-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 5px;

        .day-number {
          text-align: center;
          font-size: 1.5em;
          font-weight: bold;
          width: 14.28%;
        }
      }

      .days-names {
        display: flex;
        justify-content: space-between;

        .day-name {
          text-align: center;
          width: 14.28%;
        }
      }
    }
  }
</style>
